package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.exponentialDecay
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.calculatePan
import androidx.compose.foundation.gestures.calculateZoom
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.util.VelocityTracker
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

@Composable
expect fun PhotoViewerDialog(
    imageBytes: ByteArray?,
    isLoading: Boolean,
    onSave: (ByteArray) -> Unit,
    onDelete: () -> Unit,
    onDismiss: () -> Unit
)

@Composable
internal fun PhotoViewerDialogCompose(
    imageBytes: ByteArray?,
    isLoading: Boolean,
    onSave: (ByteArray) -> Unit,
    onDelete: () -> Unit,
    onDismiss: () -> Unit
) {
    var rotationDegrees by remember { mutableIntStateOf(0) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var imageBitmap by remember { mutableStateOf<ImageBitmap?>(null) }
    val coroutineScope = rememberCoroutineScope()

    var scale by remember { mutableFloatStateOf(1f) }
    val offsetX = remember { Animatable(0f) }
    val offsetY = remember { Animatable(0f) }
    val decay = exponentialDecay<Float>(frictionMultiplier = 1.8f)
    var containerSize by remember { mutableStateOf(IntSize.Zero) }

    val displayGeometry = remember(scale, containerSize, imageBitmap, rotationDegrees) {
        computeDisplayGeometry(scale, containerSize, imageBitmap, rotationDegrees)
    }
    val rotFitScale = displayGeometry.rotFitScale
    val maxOffsetX = displayGeometry.maxOffX
    val maxOffsetY = displayGeometry.maxOffY

    LaunchedEffect(maxOffsetX, maxOffsetY) {
        offsetX.updateBounds(-maxOffsetX, maxOffsetX)
        offsetY.updateBounds(-maxOffsetY, maxOffsetY)
    }

    LaunchedEffect(rotationDegrees) {
        scale = 1f
        offsetX.snapTo(0f)
        offsetY.snapTo(0f)
    }

    val animatedRotation by animateFloatAsState(
        targetValue = rotationDegrees.toFloat(),
        animationSpec = tween(durationMillis = 300),
        label = "photo_rotation"
    )

    LaunchedEffect(imageBytes) {
        imageBitmap = imageBytes?.let {
            withContext(Dispatchers.Default) { it.toImageBitmap() }
        }
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = fullscreenDialogProperties()
    ) {
        DialogTransparentSystemBars()

        // Image fills the entire black canvas. Control rows float over it as overlays
        // so the image gets the maximum possible screen real estate.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black)
                .onSizeChanged { containerSize = it },
            contentAlignment = Alignment.Center
        ) {
            when {
                isLoading -> CircularProgressIndicator(color = Color.White)
                imageBitmap != null -> Image(
                    bitmap = imageBitmap!!,
                    contentDescription = "Photo",
                    modifier = Modifier
                        .fillMaxSize()
                        .pointerInput(Unit) {
                            awaitEachGesture {
                                val velocityTracker = VelocityTracker()

                                awaitFirstDown(requireUnconsumed = false)
                                coroutineScope.launch {
                                    offsetX.stop()
                                    offsetY.stop()
                                }

                                do {
                                    val event = awaitPointerEvent()
                                    val zoom = event.calculateZoom()
                                    val pan = event.calculatePan()

                                    val newScale = (scale * zoom).coerceIn(1f, 5f)
                                    scale = newScale

                                    if (newScale == 1f) {
                                        coroutineScope.launch {
                                            offsetX.snapTo(0f)
                                            offsetY.snapTo(0f)
                                        }
                                    } else {
                                        val geo = computeDisplayGeometry(scale, containerSize, imageBitmap, rotationDegrees)
                                        coroutineScope.launch {
                                            offsetX.snapTo((offsetX.value + pan.x).coerceIn(-geo.maxOffX, geo.maxOffX))
                                            offsetY.snapTo((offsetY.value + pan.y).coerceIn(-geo.maxOffY, geo.maxOffY))
                                        }
                                    }

                                    if (event.changes.size == 1) {
                                        val c = event.changes[0]
                                        velocityTracker.addPosition(c.uptimeMillis, c.position)
                                    }

                                    event.changes.forEach { it.consume() }
                                } while (event.changes.any { it.pressed })

                                if (scale > 1f) {
                                    val geo = computeDisplayGeometry(scale, containerSize, imageBitmap, rotationDegrees)
                                    offsetX.updateBounds(-geo.maxOffX, geo.maxOffX)
                                    offsetY.updateBounds(-geo.maxOffY, geo.maxOffY)
                                    val velocity = velocityTracker.calculateVelocity()
                                    coroutineScope.launch { offsetX.animateDecay(velocity.x, decay) }
                                    coroutineScope.launch { offsetY.animateDecay(velocity.y, decay) }
                                }
                            }
                        }
                        .pointerInput(Unit) {
                            detectTapGestures(
                                onDoubleTap = {
                                    coroutineScope.launch {
                                        scale = 1f
                                        offsetX.snapTo(0f)
                                        offsetY.snapTo(0f)
                                    }
                                }
                            )
                        }
                        .graphicsLayer {
                            scaleX = rotFitScale * scale
                            scaleY = rotFitScale * scale
                            translationX = offsetX.value
                            translationY = offsetY.value
                            rotationZ = animatedRotation
                        },
                    contentScale = ContentScale.Fit
                )
                else -> Text("No image available", color = Color.White)
            }

            // Top overlay: close + rotate
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.TopStart)
                    .background(Color.Black.copy(alpha = 0.85f))
                    .statusBarsPadding()
                    .padding(horizontal = 4.dp, vertical = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onDismiss) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = "Close",
                        tint = Color.White
                    )
                }
                IconButton(
                    onClick = { rotationDegrees += 90 },
                    enabled = imageBytes != null && !isLoading && !isSaving
                ) {
                    Icon(
                        imageVector = Icons.Default.Refresh,
                        contentDescription = "Rotate 90°",
                        tint = if (imageBytes != null && !isLoading && !isSaving) Color.White
                               else Color.White.copy(alpha = 0.38f)
                    )
                }
            }

            // Bottom overlay: delete + save
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.BottomStart)
                    .background(Color.Black.copy(alpha = 0.85f))
                    .navigationBarsPadding()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                OutlinedButton(
                    onClick = { showDeleteConfirm = true },
                    enabled = imageBytes != null && !isSaving,
                    colors = ButtonDefaults.outlinedButtonColors(
                        contentColor = MaterialTheme.colorScheme.error
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Delete,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp)
                    )
                    Spacer(Modifier.width(4.dp))
                    Text("Delete")
                }

                Button(
                    onClick = {
                        val bytes = imageBytes ?: return@Button
                        coroutineScope.launch {
                            isSaving = true
                            val degrees = rotationDegrees % 360
                            val rotated = if (degrees == 0) bytes else bytes.rotateImageBytes(degrees)
                            onSave(rotated)
                        }
                    },
                    enabled = imageBytes != null && !isSaving && !isLoading
                ) {
                    if (isSaving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = MaterialTheme.colorScheme.onPrimary,
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text("Save")
                    }
                }
            }
        }
    }

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Delete Photo",
        message = "Remove this photo? This action cannot be undone.",
        confirmText = "Delete",
        onConfirm = {
            showDeleteConfirm = false
            onDelete()
        },
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )
}

private data class DisplayGeometry(val rotFitScale: Float, val maxOffX: Float, val maxOffY: Float)

/**
 * Computes display geometry for the image given container size, rotation, and user zoom.
 *
 * rotFitScale compensates for the shrinkage that graphicsLayer rotationZ causes when
 * ContentScale.Fit has already sized the image based on original (unrotated) pixel dims.
 * After a 90°/270° rotation the visual size becomes (dispH × dispW); rotFitScale re-fills
 * the container from that size. For 0°/180° it is always 1f.
 *
 * maxOffX/Y are pan clamping limits derived from the final on-screen visual size.
 */
private fun computeDisplayGeometry(
    scale: Float,
    containerSize: IntSize,
    imageBitmap: ImageBitmap?,
    rotationDegrees: Int
): DisplayGeometry {
    val bitmap = imageBitmap ?: return DisplayGeometry(1f, 0f, 0f)
    if (containerSize == IntSize.Zero) return DisplayGeometry(1f, 0f, 0f)
    val cw = containerSize.width.toFloat()
    val ch = containerSize.height.toFloat()
    val imgW = bitmap.width.toFloat()
    val imgH = bitmap.height.toFloat()
    val baseScale = minOf(cw / imgW, ch / imgH)
    val dispW = imgW * baseScale
    val dispH = imgH * baseScale
    val isSwapped = rotationDegrees % 180 != 0
    val rotFitScale = if (isSwapped) minOf(cw / dispH, ch / dispW) else 1f
    val visW = if (isSwapped) dispH * rotFitScale else dispW
    val visH = if (isSwapped) dispW * rotFitScale else dispH
    val maxOffX = maxOf(0f, (visW * scale - cw) / 2f)
    val maxOffY = maxOf(0f, (visH * scale - ch) / 2f)
    return DisplayGeometry(rotFitScale, maxOffX, maxOffY)
}
